// The header's search (BL-134, pages/ux-full app.js's .search): what the box asks (search.all), when,
// and its answers as one list grouped as the office reads them, each saying where it opens. The server
// decides what each person may find; this only draws it and moves through it by the keyboard.
import { OFFICE_PERMS } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { money } from '../books/model.ts';
import { customerHref } from '../history/customer-link.ts';
import { docHref } from '../shared/doc-link.ts';
import { jobStatusWord } from '@flowology/ui-web/words';

/** Who has the box: the office, who search customers (customers.search), never the warehouse. */
export const maySearch = (held: readonly string[]): boolean => holdsAny(held, OFFICE_PERMS);

/** Typing waits this long before asking: one question for a word, not one a letter. */
export const SEARCH_WAIT_MS = 250;
/** The fewest characters it asks with; a name needs three (the server's index). A number is found
 * whole, so a job's "3" (or "#3") is asked at once. */
export const SEARCH_MIN = 2;
const NUMBER = /^#?\d+$/;

/** What the box asks with, or null while it's too short to. */
export function searchInput(text: string): { query: string; limit: number } | null {
  const query = text.trim().slice(0, 100);
  return query.length >= SEARCH_MIN || NUMBER.test(query) ? { query, limit: 5 } : null;
}

/** search.all's answer. */
export interface Found {
  customers: { id: string; number: string; displayName: string }[];
  jobs: { id: string; number: string; status: string; summary: string; customerName: string }[];
  invoices: { id: string; number: string; status: string; customerName: string; totalCents: number }[];
  estimates: {
    id: string;
    number: string;
    title: string;
    jobId: string;
    jobNumber: string;
    customerName: string;
  }[];
  phones: { phone: string; contactName: string; customerId: string; customerName: string }[];
}

/** Where an answer opens: a customer's record, a document's link, or a job's sheet over the page. */
export type Opens = { href: string } | { job: string };

export interface Hit {
  key: string;
  title: string;
  detail: string;
  opens: Opens;
}
export interface HitGroup {
  name: string;
  hits: Hit[];
}

// a job says the app's one word for its status (job-words); invoices and estimates keep these
const DOC_WORDS: Record<string, string> = {
  draft: 'Draft',
  finalized: 'Not sent',
  sent: 'Sent',
  partially_paid: 'Part paid',
  paid: 'Paid',
  void: 'Void',
  presented: 'Presented',
  sold: 'Sold',
  declined: 'Declined',
  expired: 'Expired',
};
const word = (status: string) => DOC_WORDS[status] ?? status;
const line = (...parts: string[]) => parts.filter(Boolean).join(' · ');

/** The answers in the box's order, each group with something in it: customers, jobs, invoices, estimates, phones. */
export function hitGroups(f: Found): HitGroup[] {
  const groups: HitGroup[] = [
    {
      name: 'Customers',
      hits: f.customers.map((c) => ({
        key: `customer:${c.id}`,
        title: c.displayName,
        detail: `Customer ${c.number}`,
        opens: { href: customerHref(c.id) },
      })),
    },
    {
      name: 'Jobs',
      hits: f.jobs.map((j) => ({
        key: `job:${j.id}`,
        title: `Job ${j.number}`,
        detail: line(j.customerName, j.summary, jobStatusWord(j.status)),
        opens: { job: j.id },
      })),
    },
    {
      name: 'Invoices',
      hits: f.invoices.map((i) => ({
        key: `invoice:${i.id}`,
        title: `Invoice ${i.number}`,
        detail: line(i.customerName, money(i.totalCents), word(i.status)),
        opens: { href: docHref({ kind: 'invoice', id: i.id }) },
      })),
    },
    {
      name: 'Estimates',
      hits: f.estimates.map((e) => ({
        key: `estimate:${e.id}`,
        title: `Estimate ${e.number}`,
        detail: line(e.customerName, e.title, `on job ${e.jobNumber}`),
        // an estimate is built, presented and sold on its job
        opens: { job: e.jobId },
      })),
    },
    {
      name: 'Phone numbers',
      hits: f.phones.map((p, i) => ({
        key: `phone:${p.customerId}:${i}`,
        title: p.phone,
        detail: p.contactName === p.customerName ? p.customerName : line(p.contactName, p.customerName),
        opens: { href: customerHref(p.customerId) },
      })),
    },
  ];
  return groups.filter((g) => g.hits.length);
}

/** Every answer in the order the arrows move through them. */
export const allHits = (groups: readonly HitGroup[]): Hit[] => groups.flatMap((g) => g.hits);

/** The answer the arrows move to from `at` (-1: none yet), round from the last to the first. */
export function step(at: number, count: number, by: 1 | -1): number {
  if (count <= 0) return -1;
  if (at < 0) return by === 1 ? 0 : count - 1;
  return (at + by + count) % count;
}

/** Whether `/` should go to the box: not while typing somewhere else, nor with a modifier held. */
export function takesSlash(e: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  target: { tagName?: string; isContentEditable?: boolean } | null;
}): boolean {
  if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return false;
  const tag = e.target?.tagName?.toUpperCase();
  return !(tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable);
}
